import { useEffect, useState, useRef } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { auth, db } from "../firebase";
import { collection, getDocs, getDoc, doc, query, where, limit } from "firebase/firestore";
import logo from "../assets/imgiconlogo.png";
import ChatAssistant from "../components/ChatAssistant";
import useCurrentUser from "../hooks/useCurrentUser";
import ImpersonationBanner from "../components/admin/ImpersonationBanner";
import SecuritySettings from "../components/security/SecuritySettings";
import { useImpersonation } from "../context/ImpersonationContext";
import {
  LayoutDashboard,
  PackagePlus,
  MessageSquare,
  Truck,
  Box,
  ClipboardList,
  History,
  Database,
  CreditCard,
  Bell,
  Search,
  Users,
  Cpu,
  LogOut,
  ShieldCheck,
  TrendingUp,
  Activity,
  Building2,
  Megaphone,
  AlertCircle,
  X,
  Moon,
  Sun,
  Menu,
  FileSpreadsheet,
  Mail,
  Banknote,
  RefreshCw,
  ShoppingCart,
  Warehouse,
  BarChart2,
  PackageCheck,
  Package,
  FileText,
  Link2,
  Globe,
  Radio,
  Crosshair,
  MailCheck,
  Ship,
} from "lucide-react";
import NotificationDropdown from "../components/NotificationDropdown";
import { HASBRO_BRAND, isHasbroRole } from "../components/hasbro/hasbroTheme";

// Default TinyGlobal look; tenant workspaces (Hasbro) swap in their own brand
const TINYGLOBAL_BRAND = {
  logo,
  name: "TinyGlobal",
  tagline: null,
  sidebarBg: "radial-gradient(circle at 30% 20%, #0b2a42, #071d2e)",
  headerBg: "radial-gradient(circle at 70% 50%, #0b2a42, #071d2e)",
  activeLink: "bg-gold-500/20 text-white border border-gold-500/30",
  activeIcon: "text-gold-400 shrink-0",
  avatar: "bg-gold-500 text-navy-900",
  sectionHeader: "text-slate-600",
};

// Core modules always visible to clients (beginner mode)
export const CORE_MODULES = ["shipments", "messages", "tracking", "support"];

// ✅ Navigation structure — each client item has a `module` key for progressive disclosure
// Exported so RelationMapModal can scan for unregistered components
export const navItems = {
  client: [
    { label: "Dashboard", path: "/client", icon: LayoutDashboard, module: "shipments" },
    { label: "Create Shipment", path: "/client/create-shipment", icon: PackagePlus, module: "shipments" },
    { label: "Shipment Tracking", path: "/client/customs", icon: Truck, module: "tracking" },
    { label: "Chat with Partners", path: "/client/chat", icon: MessageSquare, module: "messages" },

    { label: "TOOLS", path: null, module: "tools" },
    { label: "Landing Evaluations", path: "/client/landing-evaluations", icon: Search, module: "tools" },
    { label: "HS Code Evaluations", path: "/client/hs-evaluations", icon: Search, module: "tools" },
    { label: "Inventory (3PL)", path: "/client/inventory", icon: Box, module: "inventory" },
    { label: "Inbound Shipments", path: "/client/inbound", icon: PackageCheck, module: "inventory" },
    { label: "Order Admin", path: "/client/order-admin", icon: ClipboardList, module: "orders" },

    { label: "COMPLIANCE", path: null, module: "compliance" },
    { label: "ATT Accreditation", path: "/client/att-accreditation", icon: ShieldCheck, module: "compliance" },
    { label: "Workflow", path: "/client/tools/workflow", icon: Cpu, module: "workflows" },
    { label: "Stock Reconciliation", path: "/client/stock-reconciliation", icon: Box, module: "inventory" },

    { label: "DATA", path: null, module: "data" },
    { label: "Master Data", path: "/client/master-data", icon: Database, module: "data" },
    { label: "General History", path: "/client/history", icon: History, module: "data" },
    { label: "Payable Accounts", path: "/client/payables", icon: CreditCard, module: "financials" },
    { label: "API Partners", path: "/client/api-partners", icon: Users, module: "api" },

    { label: "SUPPORT", path: null, module: "support" },
    { label: "My Support Tickets", path: "/client/support", icon: MessageSquare, module: "support" },
  ],
  operator: [
    { label: "Dashboard", path: "/operator", icon: LayoutDashboard },
    { label: "Manage Requests", path: "/operator/manage-requests", icon: ClipboardList },
    { label: "Messages", path: "/operator/chat", icon: MessageSquare },
  ],
  admin: [
    { label: "Dashboard", path: "/admin", icon: LayoutDashboard },

    { label: "MANAGEMENT", path: null },
    { label: "Organizations", path: "/admin/management", icon: Building2 },
    { label: "Subscriptions", path: "/admin/subscriptions", icon: CreditCard },

    { label: "OPERATIONS", path: null },
    { label: "Control Tower", path: "/admin/control-tower", icon: Radio },
    { label: "All Shipments", path: "/admin/all-shipments", icon: Truck },
    { label: "Support Tickets", path: "/admin/tickets", icon: MessageSquare },
    { label: "Platform Announcements", path: "/admin/announcements", icon: Bell },
    { label: "HS Code Management", path: "/admin/hs-codes", icon: Database },

    { label: "ANALYTICS", path: null },
    { label: "Revenue Analytics", path: "/admin/revenue", icon: TrendingUp },
    { label: "Traffic Analytics", path: "/admin/traffic", icon: Globe },
    { label: "Global Audit", path: "/admin/audit", icon: ShieldCheck },
    { label: "System Health", path: "/admin/health", icon: Activity },

    { label: "TOOLS", path: null },
    { label: "Excel Converter", path: "/admin/excel-converter", icon: FileSpreadsheet },
    { label: "Email Center", path: "/admin/email-center", icon: Mail },

    { label: "FINANCE", path: null },
    { label: "Payments & Invoices", path: "/admin/payments", icon: Banknote },
    { label: "Financial Reports", path: "/admin/financial-reports", icon: BarChart2 },
    { label: "SLA Contracts", path: "/admin/sla", icon: FileText },

    { label: "ERP & PROCUREMENT", path: null },
    { label: "Suppliers", path: "/admin/suppliers", icon: Building2 },
    { label: "Purchase Orders", path: "/admin/purchase-orders", icon: ShoppingCart },
    { label: "Warehouses", path: "/admin/warehouses", icon: Warehouse },
    { label: "ERP Sync", path: "/admin/erp-sync", icon: RefreshCw },
    { label: "EDI Management", path: "/admin/edi", icon: FileText },
    { label: "Integration Hub", path: "/admin/integrations", icon: Link2 },
  ],
  agent: [
    { label: "ATT Review Portal", path: "/agent", icon: ShieldCheck },
    { label: "Customs Dashboard", path: "/agent/customs", icon: LayoutDashboard },
    { label: "Customs Entries", path: "/agent/entries", icon: ClipboardList },
    { label: "Landing Evaluations", path: "/agent/landing-evaluations", icon: Search },
    { label: "DG Shipments", path: "/agent/dg-shipments", icon: Search },
    { label: "My Support Tickets", path: "/agent/support", icon: MessageSquare },
  ],
  "3pl": [
    { label: "3PL Dashboard", path: "/3pl", icon: LayoutDashboard },
    { label: "Warehouse Management (WMS)", path: "/3pl/wms", icon: Box },
    { label: "Inbound Receiving", path: "/3pl/inbound", icon: PackageCheck },
    { label: "Operational Data", path: "/3pl/ops", icon: Database },
  ],
  vendor: [
    { label: "Vendor Dashboard", path: "/vendor", icon: LayoutDashboard },
    { label: "Inbound Shipments (ASN)", path: "/vendor/inbound", icon: Package },
  ],
  customer: [
    { label: "My Dashboard", path: "/customer", icon: LayoutDashboard },
    { label: "My Shipments", path: "/customer/shipments", icon: Truck },
    { label: "Notifications", path: "/customer/alerts", icon: Bell },
  ],
  hasbro: [
    { label: "Control Tower", path: "/hasbro", icon: Radio },
    { label: "Imports & Exports", path: "/hasbro/imports-exports", icon: Ship },
    { label: "CUSTOMER CARE", path: null },
    { label: "Customer Tickets", path: "/hasbro/tickets", icon: MessageSquare },
    { label: "Customer Accounts", path: "/hasbro/customers", icon: Users },
    { label: "Sent Emails", path: "/hasbro/emails", icon: MailCheck },
    { label: "INTEGRATIONS", path: null },
    { label: "Partners & Vendors (API)", path: "/hasbro/integrations", icon: Link2 },
    { label: "GOVERNANCE", path: null },
    { label: "Audit Log", path: "/hasbro/audit", icon: ShieldCheck },
  ],
  "hasbro-customer": [
    { label: "My Tickets", path: "/hasbro-customer", icon: MessageSquare },
  ],
  "super-admin": [
    { label: "Platform Overview", path: "/super-admin", icon: LayoutDashboard },
    { label: "Organizations", path: "/super-admin/management", icon: Building2 },
    { label: "Support Tickets", path: "/super-admin/tickets", icon: MessageSquare },
    { label: "Platform Announcements", path: "/super-admin/announcements", icon: Bell },
    { label: "HS Code Management", path: "/super-admin/hs-codes", icon: Database },
    { label: "Subscriptions", path: "/super-admin/subscriptions", icon: CreditCard },
    { label: "Revenue Analytics", path: "/super-admin/revenue", icon: TrendingUp },
    { label: "System Health", path: "/super-admin/health", icon: Activity },
    { label: "Traffic Analytics", path: "/super-admin/traffic", icon: Globe },
    { label: "Control Tower",  path: "/super-admin/control-tower",  icon: Radio },
    { label: "SME Prospector", path: "/super-admin/sme-prospector", icon: Crosshair },
    { label: "Global Audit", path: "/super-admin/audit", icon: ShieldCheck },
    { label: "Excel Converter", path: "/super-admin/excel-converter", icon: FileSpreadsheet },
    { label: "Email Center", path: "/super-admin/email-center", icon: Mail },

    { label: "FINANCE", path: null },
    { label: "Payments & Invoices", path: "/super-admin/payments", icon: Banknote },
    { label: "Financial Reports", path: "/super-admin/financial-reports", icon: BarChart2 },
    { label: "SLA Contracts", path: "/super-admin/sla", icon: FileText },

    { label: "ERP & PROCUREMENT", path: null },
    { label: "Suppliers", path: "/super-admin/suppliers", icon: Building2 },
    { label: "Purchase Orders", path: "/super-admin/purchase-orders", icon: ShoppingCart },
    { label: "Warehouses", path: "/super-admin/warehouses", icon: Warehouse },
    { label: "ERP Sync", path: "/super-admin/erp-sync", icon: RefreshCw },
    { label: "EDI Management", path: "/super-admin/edi", icon: FileText },
    { label: "Integration Hub", path: "/super-admin/integrations", icon: Link2 },
  ],
};

// A live listener the security rules refuse is logged once, quietly — not an uncaught error
const couldNotLoad = what => err => console.warn(`Couldn't load ${what} (${err.code || err.message})`);

export default function DashboardLayout({ role }) {
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [newMessages, setNewMessages] = useState(false);
  const [newRequests, setNewRequests] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const sidebarRef = useRef(null);
  const { clientId, userName, userPermissions, enabledModules, themePreference, setThemePreference } = useCurrentUser();
  const { impersonation } = useImpersonation();
  const [tenantFeatures, setTenantFeatures] = useState(null);
  const brand = isHasbroRole(role) ? HASBRO_BRAND : TINYGLOBAL_BRAND;

  // Local theme state for immediate updates
  const [currentTheme, setCurrentTheme] = useState("light");

  // Mount theme
  useEffect(() => {
    // Determine initial theme
    // 1. User Preference
    // 2. Local Storage
    // 3. System Preference
    let resolvedTheme = "light";
    if (themePreference) {
      resolvedTheme = themePreference;
    } else if (localStorage.getItem("theme")) {
      resolvedTheme = localStorage.getItem("theme");
    } else if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      resolvedTheme = "dark";
    }

    setCurrentTheme(resolvedTheme);
    if (resolvedTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [themePreference]);

  const toggleTheme = () => {
    const newTheme = currentTheme === "light" ? "dark" : "light";
    setCurrentTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    if (setThemePreference) {
      setThemePreference(newTheme);
    }
    if (newTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  useEffect(() => {
    // Read once when the layout loads (sign-in or a page reload) — no live listeners, so an
    // open tab doesn't read the database on its own. The "new" dots only need to know a
    // collection isn't empty, so limit(1) reads one document.
    let cancelled = false;
    const notEmpty = (q, set, what) => getDocs(query(q, limit(1)))
      .then(snap => { if (!cancelled && !snap.empty) set(true); })
      .catch(couldNotLoad(what));

    if (role === "operator") {
      notEmpty(collection(db, "chats", "client-operator", "messages"), setNewMessages, "operator chat");
      notEmpty(collection(db, "shipmentRequests"), setNewRequests, "shipment requests");
    }

    if (role === "client" && clientId) {
      notEmpty(collection(db, "chats", `${clientId}_chat`, "messages"), setNewMessages, "client chat");
      getDoc(doc(db, "companies", clientId))
        .then(docSnap => { if (!cancelled) setTenantFeatures(docSnap.exists() ? docSnap.data().enabledFeatures || null : []); })
        .catch(couldNotLoad("company features"));
    }

    return () => { cancelled = true; };
  }, [role, clientId]);

  // Close mobile menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);

  useEffect(() => {
    // Loaded once with the layout; only active announcements are shown anyway
    let cancelled = false;
    getDocs(query(collection(db, "announcements"), where("status", "==", "active")))
      .then(snap => { if (!cancelled) setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() }))); })
      .catch(couldNotLoad("announcements"));
    return () => { cancelled = true; };
  }, []);

  const activeAnnouncements = announcements.filter(a => {
    if (dismissedAnnouncements.includes(a.id)) return false;
    if (a.status !== 'active') return false;
    if (a.expiresAt?.toDate && a.expiresAt.toDate() < new Date()) return false;
    if (a.targetType === 'allUsers') return true;
    if (a.targetType === 'allTenants' && ['client', 'agent', '3pl'].includes(role)) return true;
    if (a.targetType === 'tenant' && a.targetId === clientId) return true;
    return false;
  });

  const FEATURE_REQUIREMENTS = {
    "/client/create-shipment": { tenant: "shipments", user: "shipments_create" },
    "/client/customs": { tenant: "customs", user: "customs_view" },
    "/client/inventory": { tenant: "inventory", user: "inventory_view" },
    "/client/stock-reconciliation": { tenant: "inventory", user: "inventory_view" },
    "/client/support": null, // Available if logged in
    "/client/chat": { tenant: "chat", user: "chat_access" },
    "/client/hs-evaluations": { tenant: "hscode", user: "customs_view" },
    "/client/landing-evaluations": { tenant: "analytics", user: "reports_view" },
    "/client/order-admin": { tenant: "tms", user: "tms_view" },
    "/client/payables": { tenant: "reports", user: "reports_view" },
    "/client/history": { tenant: "reports", user: "reports_view" }
  };

  const filteredLinks =
    navItems[role]?.filter((item) => {
      if (!item.label.toLowerCase().includes(search.toLowerCase())) return false;

      // Progressive disclosure: only filter when admin has explicitly set a module list
      if (role === 'client' && item.module && enabledModules !== null && enabledModules.length > 0) {
        if (!item.path) {
          // Section header: hide if no sibling in this module group is allowed
          if (!enabledModules.includes(item.module)) return false;
        } else {
          if (!enabledModules.includes(item.module)) return false;
        }
      }

      // Tenant feature gate: only restrict when company has an explicit non-empty feature list
      // AND the user has an explicit non-empty permission list
      if (role === 'client' && tenantFeatures !== null && tenantFeatures.length > 0 && item.path) {
        const reqs = FEATURE_REQUIREMENTS[item.path];
        if (reqs) {
          if (reqs.tenant && !tenantFeatures.includes(reqs.tenant)) return false;
          if (reqs.user && userPermissions && userPermissions.length > 0 && !userPermissions.includes(reqs.user)) return false;
        }
      }
      return true;
    }) || [];

  const displayLinks = [];
  for (let i = 0; i < filteredLinks.length; i++) {
    const item = filteredLinks[i];
    if (!item.path) {
      let hasChildren = false;
      for (let j = i + 1; j < filteredLinks.length; j++) {
        if (filteredLinks[j].path) {
          hasChildren = true;
          break;
        }
        if (!filteredLinks[j].path) {
          break;
        }
      }
      if (hasChildren) {
        displayLinks.push(item);
      }
    } else {
      displayLinks.push(item);
    }
  }

  const renderBadge = (path) => {
    if (path?.includes("chat") && newMessages) {
      return (
        <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
          NEW
        </span>
      );
    }
    if (path?.includes("manage-requests") && newRequests) {
      return (
        <span className="ml-auto bg-gold-500 text-navy-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
          NEW
        </span>
      );
    }
    return null;
  };

  return (
    <div className={`min-h-[100dvh] flex bg-slate-50 dark:bg-slate-900 font-sans text-slate-900 dark:text-slate-200 selection:bg-navy-900 dark:selection:bg-gold-500 selection:text-white transition-colors duration-300 overflow-hidden ${impersonation ? "pt-10" : ""}`}>

      <ImpersonationBanner />

      {/* Mobile Backdrop */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-navy-900/50 backdrop-blur-sm lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`fixed lg:static inset-y-0 left-0 w-64 flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{ background: brand.sidebarBg }}
      >
        {/* Logo Area */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl overflow-hidden border border-white/20">
              <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover" />
            </div>
            <div className="leading-tight">
              <span className="block font-bold text-base tracking-tight text-white">{brand.name}</span>
              {brand.tagline && <span className="block text-[10px] text-white/50">{brand.tagline}</span>}
            </div>
          </div>
          <button className="lg:hidden text-white/60 hover:text-white p-1" onClick={() => setMobileMenuOpen(false)}>
            <X size={18} />
          </button>
        </div>

        {/* Search Input */}
        <div className="px-4 pt-4 pb-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search navigation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 bg-white/5 border border-white/10 text-sm text-white placeholder-slate-500 rounded-xl focus:outline-none focus:ring-1 focus:ring-gold-500 focus:border-gold-500/50 transition-all"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10">
          {displayLinks.map((item, idx) =>
            item.path ? (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  if (item.path.includes("chat")) setNewMessages(false);
                  if (item.path.includes("manage-requests")) setNewRequests(false);
                  setMobileMenuOpen(false);
                }}
                className={`group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                  location.pathname === item.path
                    ? brand.activeLink
                    : `${brand === TINYGLOBAL_BRAND ? "text-slate-400" : "text-white/70"} border border-transparent hover:bg-white/8 hover:text-white`
                }`}
              >
                {item.icon && (
                  <item.icon
                    size={17}
                    className={location.pathname === item.path ? brand.activeIcon : `${brand === TINYGLOBAL_BRAND ? "text-slate-500" : "text-white/50"} group-hover:text-slate-300 shrink-0`}
                  />
                )}
                <span className="truncate">{item.label}</span>
                {renderBadge(item.path)}
              </Link>
            ) : (
              <div
                key={`header-${idx}`}
                className={`mt-5 mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.18em] ${brand.sectionHeader}`}
              >
                {item.label}
              </div>
            )
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2 rounded-xl bg-white/5 border border-white/10">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${brand.avatar}`}>
              {(role?.[0] || "U").toUpperCase()}{(role?.[1] || "").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-white leading-tight">{userName || "User"}</p>
              <p className={`text-xs truncate capitalize ${brand === TINYGLOBAL_BRAND ? "text-slate-500" : "text-white/60"}`}>{role === "hasbro-customer" ? "Hasbro Customer" : role} Account</p>
            </div>
            <button
              onClick={() => setSecurityOpen(true)}
              className="text-slate-500 hover:text-white transition-colors p-1 shrink-0"
              title="Security · two-step sign-in"
            >
              <ShieldCheck size={15} />
            </button>
            <button
              onClick={() => { localStorage.clear(); auth.signOut().finally(() => { window.location.href = "/login"; }); }}
              className="text-slate-500 hover:text-white transition-colors p-1 shrink-0"
              title="Sign out"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>
      {securityOpen && <SecuritySettings onClose={() => setSecurityOpen(false)} />}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">


        {/* Top Header */}
        <header className="h-16 flex items-center justify-between px-4 lg:px-8 z-10 shrink-0 border-b border-white/10" style={{ background: brand.headerBg }}>

          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 -ml-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={22} />
            </button>
            <h2 className="text-base lg:text-lg font-bold text-white truncate max-w-[150px] sm:max-w-xs md:max-w-sm">
              {navItems[role]?.find(i => i.path === location.pathname)?.label || "Dashboard"}
            </h2>
          </div>

          <div className="flex items-center gap-2 lg:gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-gold-400 transition-colors rounded-xl hover:bg-white/10"
            >
              {currentTheme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <NotificationDropdown workspace={role} />
            <div className="hidden sm:block h-5 w-px bg-white/10"></div>
            <span className="hidden sm:inline-block text-sm font-medium text-slate-400">{brand === TINYGLOBAL_BRAND ? "TinyGlobal Platform" : [brand.name, brand.tagline].filter(Boolean).join(" · ")}</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-8 relative">
          {activeAnnouncements.map(announcement => (
            <div key={announcement.id} className={`mb-6 rounded-2xl shadow-sm border p-4 flex gap-4 animate-in slide-in-from-top-4 duration-300 ${announcement.priority === 'important' ? 'bg-rose-50 border-rose-200' : 'bg-blue-50 border-blue-200'}`}>
              {announcement.imageUrl && (
                <img src={announcement.imageUrl} alt="Announcement" className="w-16 h-16 object-cover rounded-xl shrink-0 bg-white" />
              )}
              <div className="flex-1 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-1">
                  {announcement.priority === 'important' ? <AlertCircle size={14} className="text-rose-600" /> : <Megaphone size={14} className="text-blue-600" />}
                  <h3 className={`font-bold text-sm ${announcement.priority === 'important' ? 'text-rose-900' : 'text-blue-900'}`}>{announcement.title}</h3>
                </div>
                <p className={`text-xs ${announcement.priority === 'important' ? 'text-rose-700' : 'text-blue-700'}`}>{announcement.message}</p>
              </div>
              <button onClick={() => setDismissedAnnouncements(prev => [...prev, announcement.id])} className={`p-2 rounded-lg transition-colors h-fit ${announcement.priority === 'important' ? 'hover:bg-rose-100 text-rose-500 hover:text-rose-800' : 'hover:bg-blue-100 text-blue-500 hover:text-blue-800'}`}>
                <X size={16} />
              </button>
            </div>
          ))}
          <Outlet />
          {role === "client" && <ChatAssistant />}
        </main>
      </div>
    </div>
  );
}
